import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, MailCheck, MailX, RefreshCcw, Clock, ArrowRight, ShieldAlert } from "lucide-react";
import { api, setToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import BrandMark from "@/components/BrandMark";
import AuthLegalFooter from "@/components/legal/AuthLegalFooter";
import { toast } from "sonner";
import { track, EVENTS } from "@/lib/analytics";

const RESEND_COOLDOWN_SEC = 60;

// Persist confirm results in sessionStorage so navigating to the same
// verification URL twice (e.g. Playwright navigation retries, or the user
// hitting refresh after confirming) shows the success screen instead of a
// spurious "expired" error caused by the single-use token being consumed.
const _TOKEN_CACHE_KEY = "cos:verify-token-results";
function _readTokenResults() {
  try {
    return JSON.parse(window.sessionStorage.getItem(_TOKEN_CACHE_KEY) || "{}");
  } catch { return {}; }
}
function _saveTokenResult(token, result) {
  try {
    const map = _readTokenResults();
    map[token] = result;
    window.sessionStorage.setItem(_TOKEN_CACHE_KEY, JSON.stringify(map));
  } catch { /* noop */ }
}

/**
 * Unified email-verification screen — covers all 6 states from the P0 spec:
 *   - Check your email (default when authenticated + unverified)
 *   - Email verified successfully (when ?token= confirms OK)
 *   - Verification link expired / invalid (when ?token= fails)
 *   - Verification email resent (transient toast after successful resend)
 *   - Resend cooldown / error (inline)
 *   - Loading state while confirming a token
 *
 * URL contract:
 *   /verify-email             → pending screen (needs authenticated session OR ?email=)
 *   /verify-email?token=…     → attempts to confirm the token, then shows result
 *   /verify-email?email=…     → public entry point (from an email link where the
 *                                user isn't logged in). Pre-fills the resend form.
 */
export default function VerifyEmail() {
  const location = useLocation();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { user, setUser } = useAuth();

  const rawToken = params.get("token") || "";
  const emailHint = params.get("email") || "";

  // State: idle | confirming | ok | expired | invalid | resending
  const [state, setState] = useState(rawToken ? "confirming" : "idle");
  const [errorDetail, setErrorDetail] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const attemptedConfirmRef = useRef(false);

  // ─── Confirm-on-mount when ?token=… is present ────────────────────────────
  useEffect(() => {
    if (!rawToken) return;
    // First check the sessionStorage cache — if we already confirmed (or
    // rejected) this exact token in this tab, replay the result rather than
    // hit the API again. Prevents "expired" false-negatives on refresh.
    const cached = _readTokenResults()[rawToken];
    if (cached) {
      setState(cached);
      return;
    }
    if (attemptedConfirmRef.current) return;
    attemptedConfirmRef.current = true;
    setState("confirming");
    api.post("/auth/email-verify/confirm", { token: rawToken })
      .then(() => {
        _saveTokenResult(rawToken, "ok");
        setState("ok");
        track(EVENTS.EMAIL_VERIFIED, { source: "token_link" });
        api.get("/auth/me")
          .then((r) => setUser(r.data))
          .catch(() => {});
      })
      .catch((err) => {
        const msg = String(err?.response?.data?.detail || "");
        const next = /expired/i.test(msg) ? "expired" : "invalid";
        _saveTokenResult(rawToken, next);
        setErrorDetail(msg);
        setState(next);
      });
  }, [rawToken, setUser]);

  // ─── Fetch masked email + verified state when authenticated ───────────────
  useEffect(() => {
    if (!user || state === "confirming" || state === "ok") return;
    api.get("/auth/email-verify/status")
      .then((r) => {
        setMaskedEmail(r.data.email || "");
        if (r.data.email_verified) {
          // Already verified — bounce them into the app.
          nav("/app", { replace: true });
        }
      })
      .catch(() => { /* not authenticated or stale token — public flow */ });
  }, [user, state, nav]);

  // ─── Resend cooldown ticker ───────────────────────────────────────────────
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const [resendEmail, setResendEmail] = useState(emailHint);
  const showResendForm = !user;   // when authed, we don't need the email input

  const handleResend = async () => {
    if (cooldown > 0) return;
    setState("resending");
    try {
      if (user) {
        // Authed path — server picks up email from JWT.
        await api.post("/auth/email-verify/send");
      } else {
        if (!resendEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resendEmail)) {
          setState("idle");
          toast.error("Please enter a valid email");
          return;
        }
        // Public resend — always returns 200 to prevent enumeration.
        await api.post("/auth/email-verify/resend", { email: resendEmail.trim() });
      }
      setState("idle");
      setCooldown(RESEND_COOLDOWN_SEC);
      toast.success("If that email needs verifying, a fresh link is on its way.");
    } catch (err) {
      setState("idle");
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("Too many requests. Please wait a minute before trying again.");
        setCooldown(RESEND_COOLDOWN_SEC);
      } else if (status === 503) {
        toast.error("Email service temporarily unavailable. Please try again shortly.");
      } else {
        toast.error(String(err?.response?.data?.detail || "Couldn't send the email. Please try again."));
      }
    }
  };

  const handleLogout = () => {
    setToken(null);
    setUser(null);
    nav("/login", { replace: true });
  };

  // ─── Content selector ─────────────────────────────────────────────────────
  const content = useMemo(() => {
    if (state === "confirming") {
      return { icon: Clock, tone: "info", title: "Verifying your email…", body: "This will only take a moment." };
    }
    if (state === "ok") {
      return {
        icon: CheckCircle2, tone: "success",
        title: "Email verified",
        body: "Thanks — your Career OS account is now active. Continue below to start where you left off.",
        cta: { label: user ? "Open Career OS" : "Log in to continue", to: user ? "/app" : "/login" },
      };
    }
    if (state === "expired") {
      return {
        icon: Clock, tone: "warn",
        title: "This verification link has expired",
        body: "For security, verification links only work for 24 hours. Request a fresh one below and check your inbox again.",
      };
    }
    if (state === "invalid") {
      return {
        icon: MailX, tone: "error",
        title: "That verification link isn't valid",
        body: "The link may have already been used, been revoked by a newer one, or been mistyped. Request a fresh link below to try again.",
      };
    }
    if (state === "resending") {
      return { icon: RefreshCcw, tone: "info", title: "Sending your verification email…" };
    }
    // idle (pending) — main "check your email" screen
    return {
      icon: MailCheck, tone: "info",
      title: "Check your email",
      body: maskedEmail
        ? `We sent a verification link to ${maskedEmail}. Click the link inside to unlock your Career OS account. It expires in 24 hours.`
        : "We sent you a verification link. Click the link inside to unlock your Career OS account. It expires in 24 hours.",
    };
  }, [state, maskedEmail, user]);

  const Icon = content.icon;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="px-6 py-6 flex items-center">
        <Link to="/" className="flex items-center gap-2">
          <BrandMark />
          <span className="font-display font-black tracking-tight">Career OS</span>
        </Link>
      </header>
      <main className="flex-1 flex items-center justify-center px-6" data-testid="verify-email-page">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-xl"
          data-testid={`verify-state-${state}`}
        >
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${
            content.tone === "success" ? "bg-[hsl(var(--success))/0.12] text-[hsl(var(--success))]"
              : content.tone === "warn" ? "bg-amber-500/10 text-amber-500"
              : content.tone === "error" ? "bg-red-500/10 text-red-500"
              : "bg-primary/10 text-primary"
          }`}>
            <Icon size={22} strokeWidth={2.4} />
          </div>
          <h1 className="font-display font-black text-2xl tracking-tight mb-2" data-testid="verify-title">
            {content.title}
          </h1>
          {content.body && (
            <p className="text-sm text-muted-foreground leading-relaxed mb-6" data-testid="verify-body">
              {content.body}
            </p>
          )}

          {/* Success CTA */}
          {state === "ok" && content.cta && (
            <Button
              onClick={() => nav(content.cta.to)}
              size="lg"
              className="w-full gap-2"
              data-testid="verify-cta-continue"
            >
              {content.cta.label} <ArrowRight size={14} />
            </Button>
          )}

          {/* Pending / expired / invalid → resend UI */}
          {(state === "idle" || state === "expired" || state === "invalid" || state === "resending") && (
            <div className="space-y-3">
              {showResendForm && (
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
                  data-testid="verify-resend-email"
                  autoComplete="email"
                />
              )}
              <Button
                onClick={handleResend}
                disabled={cooldown > 0 || state === "resending"}
                size="lg"
                className="w-full gap-2"
                data-testid="verify-resend-btn"
              >
                <RefreshCcw size={14} />
                {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend verification email"}
              </Button>
              {user ? (
                <button
                  onClick={handleLogout}
                  className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors underline underline-offset-4"
                  data-testid="verify-logout"
                >
                  Log out
                </button>
              ) : (
                <div className="text-center text-xs text-muted-foreground">
                  <Link to="/login" className="hover:text-foreground underline underline-offset-4" data-testid="verify-back-to-login">
                    Back to log in
                  </Link>
                </div>
              )}
              {errorDetail && state !== "resending" && (
                <div className="text-xs text-red-500 flex items-start gap-1.5 mt-2" data-testid="verify-error-detail">
                  <ShieldAlert size={13} className="mt-0.5 shrink-0" />
                  <span>{errorDetail}</span>
                </div>
              )}
            </div>
          )}
        </motion.div>
      </main>
      <AuthLegalFooter />
    </div>
  );
}
